// Quality tiers (stack/index.md "Quality tiers"), the benchmark pick and the auto-downgrade.
import type { QualityTier } from '../app/params';

export interface TierSettings {
  tier: QualityTier;
  furShells: number;
  shadowMapSize: number;
  softShadows: boolean;
  ao: 'off' | 'half' | 'full';
  grassBlades: number;
  crowd: number; // 0 = impostor cards
  dof: 'off' | 'closeups' | 'bokeh';
  bloom: boolean;
  smaa: boolean;
}

export const TIERS: Record<QualityTier, TierSettings> = {
  low: { tier: 'low', furShells: 0, shadowMapSize: 1024, softShadows: false, ao: 'off', grassBlades: 0, crowd: 0, dof: 'off', bloom: true, smaa: true },
  medium: { tier: 'medium', furShells: 12, shadowMapSize: 2048, softShadows: false, ao: 'half', grassBlades: 108_000, crowd: 1500, dof: 'closeups', bloom: true, smaa: true },
  high: { tier: 'high', furShells: 24, shadowMapSize: 4096, softShadows: true, ao: 'full', grassBlades: 360_000, crowd: 4000, dof: 'bokeh', bloom: true, smaa: true },
};

const ORDER: QualityTier[] = ['low', 'medium', 'high'];

export const MIN_RENDER_SCALE = 0.6;
export const MAX_RENDER_SCALE = 1.0;
const SLOW_FRAME_MS = 20;
const SLOW_HOLD_MS = 3000;
const WINDOW = 60; // frames in the p90 window
/** Each render-scale change resizes every render target, so changes are coarse and spaced out. */
const SCALE_STEP = 0.1;
const SCALE_HOLD_MS = 2000;
const VSYNC_60_MS = 17.5; // 60 Hz plus timer jitter

/** Pick a tier from benchmark frame times (ms). Pure. */
export function tierFromBenchmark(frameTimesMs: number[]): QualityTier {
  if (frameTimesMs.length === 0) return 'medium';
  const p90 = percentile(frameTimesMs, 0.9);
  if (p90 <= 11) return 'high';
  if (p90 <= 18) return 'medium';
  return 'low';
}

export function percentile(values: number[], p: number): number {
  const s = [...values].sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.floor(p * s.length))] ?? 0;
}

type Listener = (settings: TierSettings) => void;

/**
 * Holds the current tier and render scale. Feed it frame times every frame;
 * it lowers the render scale first, then drops a tier when frames stay slow at the lowest scale.
 */
export class QualityManager {
  private tierName: QualityTier;
  private scale = MAX_RENDER_SCALE;
  private times: number[] = [];
  private slowSince: number | null = null;
  private clock = 0;
  private lastScaleChange = -Infinity;
  private listeners = new Set<Listener>();
  /** False when the viewer or a URL parameter fixed the tier. */
  auto: boolean;

  constructor(initial: QualityTier, auto = true) {
    this.tierName = initial;
    this.auto = auto;
  }

  get tier(): QualityTier { return this.tierName; }
  get settings(): TierSettings { return TIERS[this.tierName]; }
  get renderScale(): number { return this.scale; }

  set(tier: QualityTier, byViewer = false): void {
    if (byViewer) this.auto = false;
    if (tier === this.tierName) return;
    this.tierName = tier;
    this.scale = MAX_RENDER_SCALE;
    this.times = [];
    this.slowSince = null;
    for (const l of this.listeners) l(this.settings);
  }

  onChange(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  /** Report one frame's duration. Returns true when the render scale changed. */
  sample(frameMs: number): boolean {
    this.clock += frameMs;
    this.times.push(frameMs);
    if (this.times.length > WINDOW) this.times.shift();
    if (this.times.length < WINDOW) return false;
    const p90 = percentile(this.times, 0.9);
    // The display's own frame interval: a 60 Hz screen never delivers frames faster than 16.7 ms,
    // so frames at that pace are capped by vsync, not slow. Screens slower than 60 Hz are not assumed.
    const display = Math.min(percentile(this.times, 0.1), VSYNC_60_MS);
    const before = this.scale;
    if (this.clock - this.lastScaleChange >= SCALE_HOLD_MS) {
      if (p90 > Math.max(SLOW_FRAME_MS * 0.85, display * 1.25)) this.scale = Math.max(MIN_RENDER_SCALE, this.scale - SCALE_STEP);
      else if (p90 < Math.max(SLOW_FRAME_MS * 0.6, display * 1.1)) this.scale = Math.min(MAX_RENDER_SCALE, this.scale + SCALE_STEP);
      this.scale = Math.round(this.scale * 100) / 100;
      if (this.scale !== before) { this.lastScaleChange = this.clock; this.times = []; }
    }

    if (this.auto && p90 > SLOW_FRAME_MS && this.scale <= MIN_RENDER_SCALE) {
      this.slowSince ??= this.clock;
      if (this.clock - this.slowSince >= SLOW_HOLD_MS) {
        const i = ORDER.indexOf(this.tierName);
        if (i > 0) this.set(ORDER[i - 1]!);
        this.slowSince = null;
        return true;
      }
    } else {
      this.slowSince = null;
    }
    return this.scale !== before;
  }
}
